import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AntageneUploadResponse {
  filename: string;
  original_filename: string;
  path: string;
  size: number;
  debug_logs?: string[];
}

export interface AntageneFileItem {
  name: string;
  size: number;
  date_import?: string | null;
  cartouches?: string;
}

export interface AntageneFilesResponse {
  items: AntageneFileItem[];
  count: number;
  debug_logs?: string[];
}

export interface AntageneInputRowPayload {
  nom_fichier: string;
  cartouche: string;
}

export interface AntageneInputCsvResponse {
  written: number;
  path: string;
  filename: string;
  input_fasta_path?: string;
  input_fasta_filename?: string;
  methode_tri_path?: string;
  methode_tri_filename?: string;
  tri_path?: string;
  tri_filename?: string;
  methode_tri?: 'pln' | 'tb' | 'pln_tb';
  chorology_filename?: string;
  tri_trace_filename?: string;
}

export interface AnalyseDepartementsTsvUploadResponse {
  analyse_id: number;
  filename: string;
  path: string;
  lines_written: number;
  e_antagene_count: number;
}

export interface AnalyseCartoucheResultatUpdateResponse {
  analyse_id: number;
  cartouche_id: number;
  fichier_resultat?: string | null;
}

export interface AntageneExtraireEspecesResponse {
  status: string;
  analyse_id?: number | null;
  mode?: 'pln' | 'tb' | 'pln_tb';
  written_mode?: 'pln' | 'tb' | 'pln_tb';
  tri_path?: string;
  excel_outputs?: string[];
  extraction?: {
    status: string;
    output?: string;
    species?: string[];
    species_count?: number;
    warnings?: string[];
    details?: Array<{
      xlsx: string;
      cartouches: string[];
      species_count: number;
    }>;
  };
  croisement?: {
    status: string;
    mode?: string;
    output?: string;
    input_species_count?: number;
    output_species_count?: number;
  };
  message?: string;
}

export interface AntageneCreateChorologieResponse {
  status: string;
  steps?: Array<{
    status: string;
    mode?: 'pln' | 'tb' | 'tb_pln';
    output?: string;
    species_count?: number;
  }>;
  message?: string;
}

export interface AnalyseCreatePayload {
  analyses: Array<{
    fichier_depart: string;
    cartouche: string;
  }>;
  methode_tri: 'pln' | 'tb' | 'pln_tb';
  type_pipe: 'antagene' | 'consensus';
  fichier_resultat?: string | null;
  departements?: string;
}

export interface AnalyseCreateBatchPayload {
  analyses: AnalyseCreatePayload[];
}

export interface AnalyseCreateResponse {
  id: number;
  methode_tri: string;
  type_pipe_id: number;
  date_analyse?: string | null;
  fichier_resultat?: string | null;
  departements?: string[];
  couples?: Array<{
    fichier_depart: string;
    cartouche: string;
    cartouche_id: number;
  }>;
}

export interface AnalyseCreateBatchResponse {
  items: AnalyseCreateResponse[];
  count: number;
  fichier_resultat?: string | null;
  departements?: string[];
  cartouches?: string[];
  cartouche_ids?: number[];
}

export interface AnalyseCartoucheResultatItem {
  cartouche_id: number;
  cartouche_nom: string;
  fichier_depart?: string | null;
  fichier_resultat?: string | null;
}

export interface AnalyseResultItem {
  id: number;
  fichiers_depart?: string[];
  methode_tri: string;
  type_pipe_id: number;
  type_pipe_nom?: string | null;
  date_analyse?: string | null;
  fichier_resultat?: string | null;
  type_hash?: string | null;
  departements?: string[];
  cartouches?: AnalyseCartoucheResultatItem[];
}

export interface AnalyseListResponse {
  items: AnalyseResultItem[];
  count: number;
  page: number;
  page_size: number;
}

export interface DownloadResultsZipPayload {
  filenames?: string[];
  include_all_results?: boolean;
  search?: string;
}

@Injectable({ providedIn: 'root' })
export class AntageneUploadService {
  private readonly baseUrl = `${environment.apiBaseUrl}/api/gestion-pipe/antagene`;

  constructor(private readonly http: HttpClient) {}

  upload(file: File): Observable<AntageneUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<AntageneUploadResponse>(`${this.baseUrl}/upload`, formData);
  }

  listFiles(): Observable<AntageneFilesResponse> {
    return this.http.get<AntageneFilesResponse>(`${this.baseUrl}/files`).pipe(
      map((res: AntageneFilesResponse) => ({
        items: res.items ?? [],
        count: res.count ?? 0,
        debug_logs: res.debug_logs ?? []
      }))
    );
  }

  listFilesSansAnalyse(): Observable<AntageneFilesResponse> {
    return this.http.get<AntageneFilesResponse>(`${this.baseUrl}/files/sans-analyse`).pipe(
      map((res: AntageneFilesResponse) => ({
        items: res.items ?? [],
        count: res.count ?? 0,
        debug_logs: [],
      }))
    );
  }

  testTbRoute(dept: string = '16'): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/test_tb`, {
      params: { dept }
    });
  }

  deleteFile(filename: string): Observable<{ deleted: boolean; filename: string }> {
    return this.http.delete<{ deleted: boolean; filename: string }>(
      `${this.baseUrl}/files/${encodeURIComponent(filename)}`
    );
  }

  writePipeInputCsv(
    rows: AntageneInputRowPayload[],
    departements = '',
    methodeTri: 'pln' | 'tb' | 'pln_tb' | '' = 'pln_tb'
  ): Observable<AntageneInputCsvResponse> {
    return this.http.post<AntageneInputCsvResponse>(`${this.baseUrl}/input-csv`, {
      rows,
      departements,
      methode_tri: methodeTri
    });
  }

  uploadAnalyseDepartementsTsv(
    analyseId: number,
    file: File,
  ): Observable<AnalyseDepartementsTsvUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<AnalyseDepartementsTsvUploadResponse>(
      `${this.baseUrl}/analyses/${analyseId}/departements-tsv`,
      formData,
    );
  }

  updateAnalyseCartoucheResultat(
    analyseId: number,
    cartoucheId: number,
    fichierResultat: string,
  ): Observable<AnalyseCartoucheResultatUpdateResponse> {
    return this.http.patch<AnalyseCartoucheResultatUpdateResponse>(
      `${this.baseUrl}/analyses/${analyseId}/cartouches/${cartoucheId}/fichier-resultat`,
      { fichier_resultat: fichierResultat }
    );
  }

  launchAntageneExtraction(mode: 'pln' | 'tb' | 'pln_tb', analyseId?: number): Observable<AntageneExtraireEspecesResponse> {
    return this.http.post<AntageneExtraireEspecesResponse>(`${this.baseUrl}/antagene/extraire-especes`, {
      mode,
      analyse_id: typeof analyseId === 'number' && Number.isInteger(analyseId) && analyseId > 0 ? analyseId : null,
    });
  }

  createChorologieFiles(): Observable<AntageneCreateChorologieResponse> {
    return this.http.post<AntageneCreateChorologieResponse>(`${this.baseUrl}/chorologie/create`, {});
  }

  createAnalyses(payload: AnalyseCreatePayload): Observable<AnalyseCreateResponse> {
    return this.http.post<AnalyseCreateResponse>(`${this.baseUrl}/analyses`, {
      analyses: payload.analyses,
      methode_tri: payload.methode_tri,
      type_pipe: payload.type_pipe,
      fichier_resultat: payload.fichier_resultat ?? null,
      departements: payload.departements ?? '',
    });
  }

  listAnalyses(page = 1, pageSize = 25, search = ''): Observable<AnalyseListResponse> {
    return this.http.get<AnalyseListResponse>(`${this.baseUrl}/analyses`, {
      params: {
        page: String(page),
        page_size: String(pageSize),
        search,
      }
    });
  }

  deleteAnalyse(analyseId: number): Observable<{ deleted: boolean; analyse_id: number; files_deleted: string[]; errors: string[]; success: boolean }> {
    return this.http.delete<{ deleted: boolean; analyse_id: number; files_deleted: string[]; errors: string[]; success: boolean }>(
      `${this.baseUrl}/analyses/${analyseId}`
    );
  }

  downloadResultsZip(payload: DownloadResultsZipPayload): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/download/results-zip`, payload, {
      responseType: 'blob'
    });
  }
}
